import React, { useState, useEffect } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LabelList
} from 'recharts';
import {
  Box,
  Typography,
  Chip,
  Stack,
  CircularProgress
} from '@mui/material';
import { API } from '../../api/api';
import GrowthMeasurementsTable from './GrowthMeasurementsTable';
import { useToast } from '../../context/ToastContext';

// Same axes for WHO, Hadlock and INTERGROWTH, so switching standards never rescales the chart.
// Weeks 14–41 cover every standard's data (WHO 14–40, INTERGROWTH 18–41; Hadlock's 10–13 is
// left off — weight is not tracked that early).
const X_DOMAIN = [14, 41];
const X_TICKS = [14, 18, 22, 26, 30, 34, 40];
// Just around the curves (WHO 2.5th at 14w is 70 g; 97.5th at 41w about 4.6 kg) so the bands get
// as much of the height as possible.
const Y_LOG_GRAMS = [65, 4800];
// Denser where most scans fall (1–4 kg): 100g, 300g, 1kg, 3kg alone left too big a jump to read.
// Each label double the one before, so they sit at equal distances on the log scale.
const Y_LOG_TICK_GRAMS = [125, 250, 500, 1000, 2000, 4000];
const Y_LINEAR_GRAMS = [0, 5000];
const Y_LINEAR_TICK_GRAMS = [0, 1000, 2000, 3000, 4000, 5000];

// Colour of the baby's line for each classification tone.
const TREND_COLORS = {
  sage: '#1B5E20', // dark green — growing well
  amber: '#D97706', // amber — needs surveillance or review
  rust: '#C62828', // red — critical
  slate: '#b47833', // not assessed: the original line colour
};

// Light background of the classification box for each tone.
const TREND_BACKGROUNDS = {
  sage: '#E8F5E9',
  amber: '#FEF3C7',
  rust: '#FDECEA',
  slate: '#F3F4F6',
};

/** "1st", "2nd", "3rd", "11th", "2.7th" — same rule as the API's signals (one decimal below 10). */
const ordinalPercentile = (percentile) => {
  const shown = percentile < 10 ? String(Math.round(percentile * 10) / 10) : String(Math.round(percentile));
  const lastTwo = Number(shown.slice(-2));
  const last = Number(shown.slice(-1));
  if (!shown.includes('.') && lastTwo >= 11 && lastTwo <= 13) return `${shown}th`;
  return `${shown}${last === 1 ? 'st' : last === 2 ? 'nd' : last === 3 ? 'rd' : 'th'}`;
};

const FetalGrowthChartLocal = ({ babyName = "Baby", patientId }) => {
  const [scale, setScale] = useState('log');
  const [showGrowCurve] = useState(false);
  const notify = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [standard, setStandard] = useState('who');
  const [standardsList, setStandardsList] = useState([]);

  useEffect(() => {
    if (!patientId) return;
    const fetchStandards = async () => {
      const res = await API.get(`/v1/patients/${patientId}/growth-chart/standards`);
      if (res && res.success) {
        setStandardsList(res.data);
      }
    };
    fetchStandards();
  }, [patientId]);

  useEffect(() => {
    if (!patientId) {
      setLoading(false);
      return;
    }
    
    const fetchData = async () => {
      setLoading(true);
      const res = await API.get(`/v1/patients/${patientId}/growth-chart?standard=${standard}&scale=${scale}`);
      if (res && res.success) {
        setData(res.data);
      }
      setLoading(false);
    };
    fetchData();
    // scale too: the axis bounds the API sends depend on it (grams vs ln grams)
  }, [patientId, standard, scale]);

  if (loading) {
    return (
      <Box sx={{ p: 3, bgcolor: '#fff', borderRadius: 2, display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!data) return null;

  const { referenceBands, patientTrend } = data;
  const isLog = scale === 'log';

  const percentilesKey = 'percentiles';

  const chartData = [];

  const inChartWeeks = (week) => week >= X_DOMAIN[0] && week <= X_DOMAIN[1];
  const bandsStartWeek = Math.min(...referenceBands.gestationalAgeWeeks);
  const bandsEndWeek = Math.max(...referenceBands.gestationalAgeWeeks);
  const maxWeek = Math.max(...referenceBands.gestationalAgeWeeks.filter(inChartWeeks));

  referenceBands.gestationalAgeWeeks.forEach((week, index) => {
    if (!inChartWeeks(week)) return;
    const dataPoint = { week };

    if (referenceBands[percentilesKey]) {
      Object.keys(referenceBands[percentilesKey]).forEach(pct => {
        let val = referenceBands[percentilesKey][pct][index];
        if (isLog) val = Math.log(val);

        dataPoint[`p${pct}`] = val;
        if (week === maxWeek) {
          dataPoint[`label${pct}`] = `${pct}th`;
        }
      });
    }

    chartData.push(dataPoint);
  });

  if (showGrowCurve && data.growCurve && data.growCurve.curve) {
    const grow = data.growCurve.curve;
    grow.gestationalAgeWeeks.forEach((week, index) => {
      let existing = chartData.find(d => d.week === week);
      if (!existing) {
        existing = { week };
        chartData.push(existing);
      }

      let median = grow.median[index];
      let p10 = grow.p10[index];
      let p90 = grow.p90[index];

      if (isLog) {
        median = Math.log(median);
        p10 = Math.log(p10);
        p90 = Math.log(p90);
      }

      existing.growMedian = median;
      existing.growP10 = p10;
      existing.growP90 = p90;

      if (week === 40) {
        existing.labelGrowMedian = 'GROW';
      }
    });
  }

  patientTrend.forEach(pt => {
    if (pt.gestationalAgeWeeks === null) return;

    let val = pt.efwGrams;
    if (isLog) val = Math.log(val);

    // "n/a" when the scan is outside the standard's weeks; the API's reason goes in the tooltip.
    const point = {
      patientValue: val,
      patientPercentileLabel: pt.efwPercentile != null ? ordinalPercentile(pt.efwPercentile) : 'n/a',
      patientPercentileNote: pt.efwPercentile != null ? null : pt.percentileNote ?? 'Percentile not available',
      gaLabel: pt.gestationalAgeLabel ?? null,
      isPatientPoint: true,
    };
    let existing = chartData.find(d => d.week === pt.gestationalAgeWeeks);
    if (existing) {
      Object.assign(existing, point);
    } else {
      chartData.push({ week: pt.gestationalAgeWeeks, ...point });
    }
  });

  chartData.sort((a, b) => a.week - b.week);

  const formatYAxis = (tickItem) => {
    const val = Math.round(isLog ? Math.exp(tickItem) : tickItem);
    if (val >= 1000) return `${Number((val / 1000).toFixed(1))}kg`;
    return `${val}g`;
  };

  const pctColors = {
    '97.5': '#b3b3b3',
    '90': '#cccccc',
    '75': '#e6e6e6',
    '50': '#d9d9d9',
    '25': '#e6e6e6',
    '10': '#cccccc',
    '2.5': '#b3b3b3',
  };

  const percentiles = ['97.5', '90', '75', '50', '25', '10', '2.5'];
  const hasPercentiles = referenceBands[percentilesKey] != null;

  const renderLabel = (props, pct) => {
    const { x, y, value } = props;
    if (!value) return null;

    let targetY = y;
    if (isLog) {
      if (pct === '97.5') targetY = y - 36;
      else if (pct === '90') targetY = y - 24;
      else if (pct === '75') targetY = y - 12;
      else if (pct === '50') targetY = y;
      else if (pct === '25') targetY = y + 12;
      else if (pct === '10') targetY = y + 24;
      else if (pct === '2.5') targetY = y + 36;
    }

    return (
      <g>
        {Math.abs(targetY - y) > 2 && (
          <line x1={x} y1={y} x2={x + 6} y2={targetY} stroke="#d1d5db" strokeWidth={1} />
        )}
        <text x={x + 8} y={targetY} fill="#9ca3af" fontSize={11} alignmentBaseline="middle">
          {value}
        </text>
      </g>
    );
  };

  // One fixed y-axis for every standard. It used to follow the API's yAxisDomain, which in log
  // view was taken as log again (bottom at ~4 g, every band squeezed into the top), and which
  // differs per standard, so switching standards rescaled the chart.
  // The baby's line follows the API's classification: green = growing well, amber = surveillance
  // or review, red = critical (urgent Doppler, FGR). Brown when there is no verdict to go on.
  const trendColor = TREND_COLORS[data.classification?.tone] ?? TREND_COLORS.slate;

  const yDomain = isLog ? Y_LOG_GRAMS.map(Math.log) : Y_LINEAR_GRAMS;
  const yTicks = isLog ? Y_LOG_TICK_GRAMS.map(Math.log) : Y_LINEAR_TICK_GRAMS;

  const getStandardLabel = (key) => {
    if (key === 'who') return 'WHO';
    if (key === 'hadlock') return 'Hadlock';
    if (key === 'intergrowth') return 'INTERGROWTH';
    return key;
  };

  if (!data) {
    return null
  }

  return (
    <Box sx={{ p: 3, bgcolor: '#fff', borderRadius: 2, }}>

      <Stack direction="row" sx={{ mb: 4, flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
        {standardsList.length > 0 ? standardsList.map((std) => {
          const isActive = std.key === standard;
          return (
            <Chip
              key={std.key}
              label={`${getStandardLabel(std.key)}${isActive ? ' ✓' : ''}`}
              onClick={() => setStandard(std.key)}
              variant={isActive ? 'filled' : 'outlined'}
              sx={{
                bgcolor: isActive ? '#e8f5e9' : 'transparent',
                color: isActive ? '#2e7d32' : 'default',
                borderColor: isActive ? '#2e7d32' : 'default',
                fontWeight: isActive ? 600 : 400,
                fontSize: "10px",
              }}
            />
          );
        }) : (
          <Chip
            label="WHO ✓"
            variant="filled"
            sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', borderColor: '#2e7d32', fontWeight: 600, fontSize: "10px", }}
          />
        )}

        <Box sx={{ flexGrow: 1 }} />

        {/* Same text whichever scale is selected: the label used to grow when Log was picked,
            pushing "Show GROW curve" onto the next line and back. Kept together so they wrap as one. */}
        <Stack direction="row" sx={{ gap: 1, flexWrap: 'nowrap' }}>
        <Chip
          label="Log scale (recommended)"
          onClick={() => setScale('log')}
          variant={scale === 'log' ? 'filled' : 'outlined'}
          sx={{
            borderColor: '#f59e0b',
            color: scale === 'log' ? '#b45309' : '#d97706',
            bgcolor: scale === 'log' ? '#fef3c7' : 'transparent',
            fontWeight: scale === 'log' ? 600 : 400,
            fontSize: "10px",
          }}
        />
        <Chip
          label="Linear scale"
          onClick={() => setScale('linear')}
          variant={scale === 'linear' ? 'filled' : 'outlined'}
          sx={{
            fontWeight: scale === 'linear' ? 600 : 400,
            borderColor: scale === 'linear' ? '#f59e0b' : 'default',
            color: scale === 'linear' ? '#b45309' : 'default',
            bgcolor: scale === 'linear' ? '#fef3c7' : 'transparent',
            fontSize: "10px",
          }}
        />
        <Chip
          label="Show GROW curve"
          // GROW is not validated for Indian mothers yet (UK coefficients): say so instead of drawing it.
          onClick={() => notify('Grow Curve is not Fully Built yet', 'info')}
          variant={showGrowCurve ? 'filled' : 'outlined'}
          sx={{
            color: showGrowCurve ? '#312e81' : '#4f46e5',
            bgcolor: showGrowCurve ? '#e0e7ff' : 'transparent',
            borderColor: '#c7d2fe',
            fontWeight: showGrowCurve ? 600 : 400,
            fontSize: "10px",
          }}
        />
        </Stack>
      </Stack>

      {/* Chart Area */}
      <Box sx={{ width: '100%', height: 520, position: 'relative' }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 50, right: 80, left: 10, bottom: 20 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" />

            <XAxis
              dataKey="week"
              type="number"
              domain={X_DOMAIN}
              ticks={X_TICKS}
              allowDataOverflow
              tickFormatter={(val) => `${val}w`}
              axisLine={false}
              tickLine={false}
              tick={{
                fill: "#000",
                fontSize: "11px",
                fontWeight: "bold",
              }}
              dy={15}
            />

            <YAxis
              domain={yDomain}
              ticks={yTicks}
              allowDataOverflow
              tickFormatter={formatYAxis}
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: '11px', fontWeight: 'bold', fill: "#000", }}
              dx={-10}
              width={60}
            />

            <Tooltip
              formatter={(value, name, item) => {
                if (name === 'patientValue') {
                  const rawGrams = isLog ? Math.exp(value) : value;
                  const point = item?.payload ?? {};
                  const percentile = point.patientPercentileNote ?? `${point.patientPercentileLabel} percentile`;
                  return [`${Math.round(rawGrams)}g — ${percentile}`, 'Estimated Fetal Weight'];
                }
                const rawGrams = isLog ? Math.exp(value) : value;
                return [`${Math.round(rawGrams)}g`, `Percentile ${name.replace('p', '')}`];
              }}
              labelFormatter={(label, items) => items?.[0]?.payload?.gaLabel ?? `Week ${label}`}
              contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}
            />

            {/* Reference bands */}
            {hasPercentiles && percentiles.map(pct => (
              <Line
                key={pct}
                type="monotone"
                dataKey={`p${pct}`}
                stroke={pctColors[pct]}
                strokeWidth={2}
                strokeDasharray="4 4"
                dot={false}
                activeDot={false}
                isAnimationActive={false}
                connectNulls
              >
                <LabelList
                  dataKey={`label${pct}`}
                  content={(props) => renderLabel(props, pct)}
                />
              </Line>
            ))}

            {/* GROW Curve */}
            {showGrowCurve && data.growCurve && (
              <>
                <Line
                  type="monotone"
                  dataKey="growMedian"
                  stroke="#6A5CAE"
                  strokeWidth={2}
                  dot={false}
                  activeDot={false}
                  isAnimationActive={false}
                  connectNulls
                >
                  <LabelList
                    dataKey="labelGrowMedian"
                    position="right"
                    style={{ fill: '#6A5CAE', fontSize: 12, fontWeight: 700 }}
                    offset={5}
                  />
                </Line>
                <Line
                  type="monotone"
                  dataKey="growP10"
                  stroke="#6A5CAE"
                  strokeWidth={1.5}
                  strokeDasharray="4 3"
                  dot={false}
                  activeDot={false}
                  isAnimationActive={false}
                  connectNulls
                />
                <Line
                  type="monotone"
                  dataKey="growP90"
                  stroke="#6A5CAE"
                  strokeWidth={1.5}
                  strokeDasharray="4 3"
                  dot={false}
                  activeDot={false}
                  isAnimationActive={false}
                  connectNulls
                />
              </>
            )}

            {/* Patient trend line — coloured by the overall verdict */}
            <Line
              type="monotone"
              dataKey="patientValue"
              stroke={trendColor}
              strokeWidth={3}
              dot={{ stroke: trendColor, strokeWidth: 2, r: 5, fill: trendColor }}
              activeDot={{ r: 7 }}
              connectNulls
            >
              <LabelList
                dataKey="patientPercentileLabel"
                position="bottom"
                offset={10}
                style={{ fill: trendColor, fontWeight: 600, fontSize: 13 }}
              />
            </Line>
          </LineChart>
        </ResponsiveContainer>
      </Box>

      {/* When the standard's curves start after the chart does (INTERGROWTH from 18w), say so, so
          the empty early weeks don't read as missing data. */}
      {bandsStartWeek > X_DOMAIN[0] && (
        <Typography sx={{ fontSize: '11px', color: '#9CA3AF', mt: 0.5, textAlign: 'center' }}>
          {data.label} covers {bandsStartWeek}–{bandsEndWeek} weeks
        </Typography>
      )}

      {/* Classification — the overall verdict, with the findings behind it */}
      {data.classification && (
        <Box
          sx={{
            mt: 3,
            px: 2,
            py: 1.25,
            borderRadius: 2,
            textAlign: 'left',
            border: `1px solid ${trendColor}33`,
            bgcolor: TREND_BACKGROUNDS[data.classification.tone] ?? TREND_BACKGROUNDS.slate,
          }}
        >
          <Stack direction="row" alignItems="center" sx={{ flexWrap: 'wrap', gap: 1.25 }}>
            <Typography sx={{ fontSize: '11px', color: '#6B7280', letterSpacing: 0.5, textTransform: 'uppercase' }}>
              Classification
            </Typography>
            <Box
              sx={{
                px: 1.25,
                py: 0.25,
                borderRadius: 10,
                bgcolor: trendColor,
                color: '#fff',
                fontSize: '11px',
                fontWeight: 700,
                letterSpacing: 0.3,
              }}
            >
              {data.classification.label}
            </Box>
            <Typography sx={{ fontSize: '13px', color: '#374151' }}>
              {data.classification.verdictSub}
            </Typography>
          </Stack>
          {Array.isArray(data.classification.reasons) && data.classification.reasons.length > 0 && (
            <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.75, mt: 1 }}>
              {data.classification.reasons.map((reason) => (
                <Box
                  key={reason}
                  sx={{
                    px: 1,
                    py: 0.25,
                    borderRadius: 1,
                    bgcolor: '#fff',
                    border: `1px solid ${trendColor}40`,
                    fontSize: '11px',
                    color: '#374151',
                  }}
                >
                  {reason}
                </Box>
              ))}
            </Stack>
          )}
        </Box>
      )}

      {/* Signals Table */}
      {data.signals && data.signals.length > 0 && (
        <Box sx={{ mt: 3, borderTop: '1px solid #eaeaea', borderLeft: '1px solid #eaeaea', borderRight: '1px solid #eaeaea', borderRadius: 0, overflow: 'hidden' }}>
          {data.signals.map((signal, idx) => {
            const getToneColor = (tone) => {
              switch (tone) {
                case 'sage': return '#507A5D';
                case 'amber': return '#A67C00';
                case 'rust': return '#A94442';
                case 'slate': return '#6B7280';
                default: return '#6B7280';
              }
            };
            const color = getToneColor(signal.tone);

            return (
              <Box
                key={idx}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  p: 1.5,
                  px: 2,
                  borderBottom: '1px solid #eaeaea'
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color }} />
                  <Typography sx={{ fontSize: '13px', color: '#1f2937' }}>
                    {signal.label}
                  </Typography>
                </Stack>
                <Typography sx={{ fontSize: '13px', color: color, fontFamily: 'monospace', fontWeight: 600 }}>
                  {signal.value}
                </Typography>
              </Box>
            );
          })}
        </Box>
      )}

      <GrowthMeasurementsTable rows={data.table} />
    </Box>
  );
};

export default FetalGrowthChartLocal;
